import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Eye, Globe, Tag, Building2, 
  Search, Filter, RefreshCw, Save, X, Upload, Image as ImageIcon,
  Calendar, MapPin, DollarSign, Users, Star, Clock
} from 'lucide-react';
import adminContentCardsService from '../../services/adminContentCardsService';

const AdminAgentWebsites = () => {
  const [activeTab, setActiveTab] = useState('content-cards');
  const [contentCards, setContentCards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCard, setEditingCard] = useState(null);

  // Form state for creating/editing content cards
  const [formData, setFormData] = useState({
    title: '',
    short_description: '',
    offer_details: '',
    main_photo: '',
    supplier_id: '',
    categories: [],
    start_date: '',
    end_date: '',
    price_from: '',
    duration: '',
    highlights: [],
    included: [],
    not_included: [],
    booking_notes: '',
    photo_gallery: [],
    allowed_agent_types: ['agent', 'agency_admin'],
    display_order: 0,
    is_active: true
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadContentCards(),
        loadCategories(),
        loadSuppliers()
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadContentCards = async () => {
    try {
      const params = {};
      if (selectedCategory) params.category = selectedCategory;
      if (selectedSupplier) params.supplier = selectedSupplier;
      
      const data = await adminContentCardsService.getAll(params);
      setContentCards(data);
    } catch (error) {
      console.error('Error loading content cards:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await adminContentCardsService.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadSuppliers = async () => {
    try {
      const data = await adminContentCardsService.getSuppliers();
      setSuppliers(data);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const handleCreateCard = () => {
    setEditingCard(null);
    setFormData({
      title: '',
      short_description: '',
      offer_details: '',
      main_photo: '',
      photo_gallery: [],
      supplier_id: '',
      category_ids: [],
      start_date: '',
      end_date: '',
      price_from: '',
      price_note: '',
      duration: '',
      highlights: [],
      included: [],
      not_included: [],
      booking_notes: '',
      allowed_agent_types: ['agent', 'agency_admin'],
      display_order: 0,
      is_active: true
    });
    setShowCreateModal(true);
  };

  const handleEditCard = (card) => {
    setEditingCard(card);
    setFormData({
      ...card,
      category_ids: card.categories?.map(c => c.id) || [],
      highlights: Array.isArray(card.highlights) ? card.highlights : [],
      included: Array.isArray(card.included) ? card.included : [],
      not_included: Array.isArray(card.not_included) ? card.not_included : [],
      photo_gallery: Array.isArray(card.photo_gallery) ? card.photo_gallery : [],
      allowed_agent_types: Array.isArray(card.allowed_agent_types) ? card.allowed_agent_types : ['agent', 'agency_admin']
    });
    setShowCreateModal(true);
  };

  const handleSaveCard = async () => {
    try {
      if (editingCard) {
        await adminContentCardsService.update(editingCard.id, formData);
      } else {
        await adminContentCardsService.create(formData);
      }
      
      setShowCreateModal(false);
      loadContentCards();
    } catch (error) {
      console.error('Error saving content card:', error);
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (window.confirm('Are you sure you want to delete this content card?')) {
      try {
        await adminContentCardsService.delete(cardId);
        loadContentCards();
      } catch (error) {
        console.error('Error deleting content card:', error);
      }
    }
  };

  const addListItem = (field, value) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
    }
  };

  const removeListItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const result = await adminContentCardsService.uploadImage(file);
      setFormData(prev => ({
        ...prev,
        main_photo: result.url
      }));
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      category_ids: prev.category_ids.includes(categoryId)
        ? prev.category_ids.filter(id => id !== categoryId)
        : [...prev.category_ids, categoryId]
    }));
  };

  const filteredCards = contentCards.filter(card => {
    const matchesSearch = card.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         card.short_description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || 
                           card.categories?.some(cat => cat.id === parseInt(selectedCategory));
    const matchesSupplier = !selectedSupplier || 
                           card.supplier_id === parseInt(selectedSupplier);
    
    return matchesSearch && matchesCategory && matchesSupplier;
  });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Globe className="w-8 h-8 mr-3 text-blue-600" />
              Agent Websites Management
            </h1>
            <p className="text-gray-600 mt-2">
              Manage global content cards, categories, and website templates for agent websites
            </p>
          </div>
          <button
            onClick={handleCreateCard}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Content Card
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'content-cards', label: 'Content Cards', icon: Tag },
            { id: 'categories', label: 'Categories', icon: Filter },
            { id: 'templates', label: 'Website Templates', icon: Globe },
            { id: 'analytics', label: 'Analytics', icon: Users }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content Cards Tab */}
      {activeTab === 'content-cards' && (
        <div>
          {/* Filters */}
          <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search content cards..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>

              <select
                value={selectedSupplier}
                onChange={(e) => setSelectedSupplier(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Suppliers</option>
                {suppliers.map(supplier => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>

              <button
                onClick={loadContentCards}
                className="flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>

          {/* Content Cards Grid */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCards.map(card => (
                <div key={card.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                  {/* Card Image */}
                  <div className="h-48 bg-gray-200 relative">
                    {card.main_photo ? (
                      <img
                        src={card.main_photo}
                        alt={card.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                    <div className="absolute top-2 right-2 flex space-x-2">
                      <button
                        onClick={() => handleEditCard(card)}
                        className="p-2 bg-white rounded-full shadow-md hover:bg-gray-50"
                      >
                        <Edit2 className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => handleDeleteCard(card.id)}
                        className="p-2 bg-white rounded-full shadow-md hover:bg-gray-50"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-4">
                    <h3 className="font-semibold text-lg text-gray-900 mb-2">
                      {card.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {card.short_description}
                    </p>

                    {/* Card Meta */}
                    <div className="space-y-2">
                      {card.supplier && (
                        <div className="flex items-center text-sm text-gray-500">
                          <Building2 className="w-4 h-4 mr-2" />
                          {card.supplier.name}
                        </div>
                      )}
                      
                      {card.categories && card.categories.length > 0 && (
                        <div className="flex items-center text-sm text-gray-500">
                          <Tag className="w-4 h-4 mr-2" />
                          {card.categories.map(cat => cat.name).join(', ')}
                        </div>
                      )}

                      {card.price_from && (
                        <div className="flex items-center text-sm text-gray-500">
                          <DollarSign className="w-4 h-4 mr-2" />
                          From ${card.price_from}
                        </div>
                      )}

                      {card.duration && (
                        <div className="flex items-center text-sm text-gray-500">
                          <Clock className="w-4 h-4 mr-2" />
                          {card.duration}
                        </div>
                      )}
                    </div>

                    {/* Status Badge */}
                    <div className="mt-3 flex justify-between items-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        card.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {card.is_active ? 'Active' : 'Inactive'}
                      </span>
                      
                      <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                        <Eye className="w-4 h-4 inline mr-1" />
                        Preview
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {filteredCards.length === 0 && !loading && (
            <div className="text-center py-12">
              <Tag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No content cards found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || selectedCategory || selectedSupplier 
                  ? 'Try adjusting your filters or search terms.'
                  : 'Create your first content card to get started.'
                }
              </p>
              {!searchTerm && !selectedCategory && !selectedSupplier && (
                <button
                  onClick={handleCreateCard}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Create Content Card
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Other tabs content would go here */}
      {activeTab === 'categories' && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h2 className="text-xl font-semibold mb-4">Categories Management</h2>
          <p className="text-gray-600">Category management interface coming soon...</p>
        </div>
      )}

      {activeTab === 'templates' && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h2 className="text-xl font-semibold mb-4">Website Templates</h2>
          <p className="text-gray-600">Template management interface coming soon...</p>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h2 className="text-xl font-semibold mb-4">Analytics & Usage</h2>
          <p className="text-gray-600">Analytics dashboard coming soon...</p>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">
                  {editingCard ? 'Edit Content Card' : 'Create New Content Card'}
                </h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter card title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Short Description
                    </label>
                    <textarea
                      value={formData.short_description}
                      onChange={(e) => setFormData(prev => ({ ...prev, short_description: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Brief description for card preview"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Supplier *
                    </label>
                    <select
                      value={formData.supplier_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, supplier_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select a supplier</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Categories *
                    </label>
                    <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-3">
                      {categories.map(category => (
                        <label key={category.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.category_ids.includes(category.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData(prev => ({
                                  ...prev,
                                  category_ids: [...prev.category_ids, category.id]
                                }));
                              } else {
                                setFormData(prev => ({
                                  ...prev,
                                  category_ids: prev.category_ids.filter(id => id !== category.id)
                                }));
                              }
                            }}
                            className="mr-2"
                          />
                          <span className="text-sm">{category.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Additional Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Additional Details</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Price From
                      </label>
                      <input
                        type="number"
                        value={formData.price_from}
                        onChange={(e) => setFormData(prev => ({ ...prev, price_from: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Duration
                      </label>
                      <input
                        type="text"
                        value={formData.duration}
                        onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., 7 days, 3 nights"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Main Photo
                    </label>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        {uploadingImage && (
                          <div className="flex items-center text-blue-600">
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                            Uploading...
                          </div>
                        )}
                      </div>
                      {formData.main_photo && (
                        <div className="mt-2">
                          <img 
                            src={formData.main_photo} 
                            alt="Preview" 
                            className="w-32 h-20 object-cover rounded-lg border border-gray-300"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="mr-2"
                    />
                    <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                      Active (visible to agents)
                    </label>
                  </div>
                </div>
              </div>

              {/* Full-width fields */}
              <div className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Detailed Description
                  </label>
                  <textarea
                    value={formData.offer_details}
                    onChange={(e) => setFormData(prev => ({ ...prev, offer_details: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Detailed description of the offer..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Booking Notes
                  </label>
                  <textarea
                    value={formData.booking_notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, booking_notes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Special booking instructions or requirements..."
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-4">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCard}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingCard ? 'Update Card' : 'Create Card'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAgentWebsites;
